/*
  Carga progresiva de productos en colecciones (scroll infinito con botón de respaldo).
  Hermanos Koumori, 2 sep 2026.

  Cómo funciona: snippets/pagination.liquid pinta un <load-more> con la URL de la
  siguiente página. Al acercarse al final (o al dar clic), se pide esa página por el
  Section Rendering API (?section_id=...), se toman los <li> del grid y se pegan al
  grid actual; el propio <load-more> se reemplaza por el que trae la página nueva,
  así el contador "Mostrando X de Y" y la URL siguiente vienen del servidor.
  Sin JS, los links de página clásicos siguen visibles.
*/
(() => {
  if (customElements.get('load-more')) return;

  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.nextUrl = this.getAttribute('data-next-url') || '';
      this.loading = false;
      this.button = this.querySelector('[data-load-more-button]');
      this.label = this.querySelector('[data-load-more-label]');
      this.loadingText = this.querySelector('[data-load-more-loading]');

      const section = this.closest('.shopify-section');
      this.sectionId = section ? section.id.replace('shopify-section-', '') : '';

      // Con JS activo los links de página se ocultan (estilo inline, porque la clase .flex del tema
      // le gana al atributo hidden); siguen en el HTML para los buscadores.
      const links = this.parentElement && this.parentElement.querySelector('[data-pagination-links]');
      if (links) links.style.display = 'none';

      if (!this.nextUrl) return;

      this.onClick = (event) => {
        event.preventDefault();
        this.load();
      };
      if (this.button) this.button.addEventListener('click', this.onClick);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        }, { rootMargin: '800px 0px' });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
    }

    setLoading(state) {
      this.loading = state;
      this.toggleAttribute('data-loading', state);
      if (this.button) this.button.disabled = state;
      if (this.label) this.label.hidden = state;
      if (this.loadingText) this.loadingText.hidden = !state;
    }

    async load() {
      if (this.loading || !this.nextUrl) return;
      this.setLoading(true);

      try {
        const url = new URL(this.nextUrl, window.location.origin);
        if (this.sectionId) url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('HTTP ' + response.status);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = this.closest('[data-fragment-id="results"]');
        const list = results && results.querySelector('ul.main-collection-items');
        const items = doc.querySelectorAll('ul.main-collection-items > li');
        if (!list || !items.length) throw new Error('la página siguiente no trajo productos');

        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(document.adoptNode(item)));
        list.appendChild(fragment);

        // La URL refleja la última página cargada, para compartir y para el botón de regresar.
        const pageUrl = new URL(this.nextUrl, window.location.origin);
        history.replaceState(
          Object.assign({}, history.state || {}, { loadMorePage: pageUrl.searchParams.get('page') }),
          '',
          pageUrl.pathname + pageUrl.search
        );

        const next = doc.querySelector('load-more');
        if (next) {
          this.replaceWith(document.adoptNode(next));
        } else {
          this.remove();
        }
      } catch (error) {
        console.warn('[load-more]', error);
        this.setLoading(false);
      }
    }
  }

  customElements.define('load-more', LoadMore);
})();
